import React, { useEffect, useState } from 'react';
import { Building2, ChevronLeft, ChevronRight, Gift, UserPlus, Users, CalendarDays } from 'lucide-react';
import { format, parseISO, addMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

const GREEN = '#2E6A4C';

const Dots = ({ used, total }) => (
  <div className="flex items-center gap-1" data-testid="societe-day-dots">
    {Array.from({ length: Math.max(total, used) }).map((_, i) => (
      <span key={i} className={`h-3 w-3 rounded-full border ${i < used ? 'bg-amber-400 border-amber-500' : 'bg-emerald-100 border-emerald-300'}`} />
    ))}
  </div>
);

const DayCard = ({ day, societe, onNewGuest }) => {
  const label = day?.date ? format(parseISO(day.date), 'EEEE d MMMM', { locale: fr }) : '';
  const canInvite = societe.configured && day.remaining_guests > 0 && day.remaining > 0;
  return (
    <div className="bg-white rounded-xl border p-3 space-y-2" data-testid="societe-day-card">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold text-neutral-800 flex items-center gap-1.5"><CalendarDays size={14} style={{ color: GREEN }} /> Droit de jeu — {label}</p>
        {societe.configured && <Dots used={day.used} total={day.quota} />}
      </div>
      {societe.configured ? (
        <>
          <p className="text-[11px] text-neutral-600" data-testid="societe-day-remaining">
            <strong>{day.remaining}</strong> joueur{day.remaining > 1 ? 's' : ''} restant{day.remaining > 1 ? 's' : ''} sur {day.quota} · <strong>{day.remaining_guests}</strong> invitation{day.remaining_guests > 1 ? 's' : ''} offerte{day.remaining_guests > 1 ? 's' : ''} disponible{day.remaining_guests > 1 ? 's' : ''}
            {societe.type === 'birdy' && day.rep_slot_times?.length > 0 && <> · vous jouez à {day.rep_slot_times.join(', ')} : l'invité est offert dans ce flight</>}
          </p>
          {day.bookings?.length > 0 && (
            <ul className="text-[11px] text-neutral-600 space-y-0.5">
              {day.bookings.map((b, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  <span className="font-medium" style={{ color: GREEN }}>{b.time}</span> {b.client_name || (b.is_rep ? 'Représentant' : 'Invité')}
                  <span className={`text-[10px] px-1 rounded ${b.is_rep ? 'bg-neutral-100 text-neutral-600' : b.invitation ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{b.is_rep ? 'représentant' : b.invitation ? 'invité offert' : 'invité payant'}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : <p className="text-[11px] text-amber-700">{societe.rule}</p>}
      <button onClick={onNewGuest} disabled={!societe.configured} className="w-full py-2 rounded-full text-xs font-semibold text-white flex items-center justify-center gap-1.5 disabled:opacity-50" style={{ backgroundColor: canInvite ? GREEN : '#8a8a8a' }} data-testid="societe-new-guest-btn">
        <UserPlus size={14} /> {canInvite ? 'Réserver pour un invité ce jour' : 'Réserver pour un invité (Green Fee payant ce jour)'}
      </button>
    </div>
  );
};

const MonthCard = ({ month, onNav }) => (
  <div className="bg-white rounded-xl border p-3 space-y-2" data-testid="societe-month-card">
    <div className="flex items-center justify-between">
      <button onClick={() => onNav(-1)} className="p-1 rounded hover:bg-neutral-100" data-testid="societe-month-prev"><ChevronLeft size={16} /></button>
      <p className="text-xs font-semibold text-neutral-800 capitalize">{format(parseISO(`${month.month}-01`), 'MMMM yyyy', { locale: fr })}</p>
      <button onClick={() => onNav(1)} className="p-1 rounded hover:bg-neutral-100" data-testid="societe-month-next"><ChevronRight size={16} /></button>
    </div>
    <div className="grid grid-cols-3 gap-2 text-center">
      <div className="rounded-lg bg-emerald-50 p-2"><p className="text-base font-bold" style={{ color: GREEN }} data-testid="societe-month-count">{month.count}</p><p className="text-[10px] text-neutral-600">invitation{month.count > 1 ? 's' : ''}</p></div>
      <div className="rounded-lg bg-emerald-50 p-2"><p className="text-base font-bold" style={{ color: GREEN }}>{month.waived_total.toFixed(0)} €</p><p className="text-[10px] text-neutral-600">de GF offerts</p></div>
      <div className="rounded-lg bg-neutral-50 p-2"><p className="text-base font-bold text-neutral-700">{month.rep_plays}</p><p className="text-[10px] text-neutral-600">parties représentants</p></div>
    </div>
    {month.invitations.length > 0 ? (
      <ul className="divide-y text-[11px]">
        {month.invitations.map(i => (
          <li key={i.booking_id} className="py-1.5 flex items-center justify-between gap-2" data-testid={`societe-invitation-${i.booking_id}`}>
            <span><span className="font-medium text-neutral-800">{format(parseISO(i.date), 'EEE d MMM', { locale: fr })}</span> · {i.time} · {i.client_name || 'Invité'}{i.rep_name ? <span className="text-neutral-500"> (par {i.rep_name})</span> : null}</span>
            <span className="inline-flex items-center gap-1 text-emerald-700"><Gift size={11} /> {i.waived ? `${i.waived.toFixed(0)} €` : 'offert'}</span>
          </li>
        ))}
      </ul>
    ) : <p className="text-[11px] text-neutral-500 text-center py-2">Aucune invitation ce mois-ci.</p>}
  </div>
);

// Sociétés représentées par le membre connecté (principale + exceptionnelles)
export const portalSocietes = (c) => [...new Set([c?.societe, ...(c?.societes_extra || [])].map(n => (n || '').trim()).filter(Boolean))];

export const SocietePicker = ({ names, value, onChange, dark = false }) => (names.length > 1 ? (
  <div className="flex flex-wrap gap-1 mt-1.5" data-testid="societe-picker">
    {names.map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`societe-pick-${n}`}
        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset transition-colors ${n === value
          ? (dark ? 'bg-white text-[#1F3D2B] ring-white' : 'bg-indigo-600 text-white ring-indigo-600')
          : (dark ? 'bg-transparent text-white/80 ring-white/40 hover:bg-white/10' : 'bg-white text-indigo-700 ring-indigo-200 hover:bg-indigo-50')}`}>
        {n}
      </button>
    ))}
  </div>
) : null);

export const SocietePortalPanel = ({ apiUrl, token, client, date, societe = '', onSocieteChange, onRebookGuest, onNewGuest }) => {
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState([]);
  const [month, setMonth] = useState(date.slice(0, 7));
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      fetch(`${apiUrl}/api/tee-times/portal/societe/summary?date=${date}&month=${month}&societe=${encodeURIComponent(societe)}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`${apiUrl}/api/tee-times/portal/my-guests`, { headers }).then(r => r.ok ? r.json() : { guests: [] }),
    ]).then(([s, g]) => { if (alive) { setData(s); setGuests(g?.guests || []); } }).finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, month, token, societe]);

  if (loading && !data) return <div className="text-center py-8 text-neutral-500 text-sm" data-testid="societe-panel-loading">Chargement...</div>;
  if (!data?.societe) return <p className="text-center text-sm text-neutral-500 py-8">Aucune société rattachée à votre fiche.</p>;
  const { societe: soc, day } = data;

  return (
    <div className="space-y-2" data-testid="societe-portal-panel">
      <div className="rounded-xl p-3 text-white" style={{ backgroundColor: '#1F3D2B' }} data-testid="societe-header-card">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-bold flex items-center gap-1.5 truncate"><Building2 size={15} /> {soc.name}</p>
          {soc.type && <span className="text-[10px] font-bold uppercase bg-white/20 px-2 py-0.5 rounded-full">{soc.type}</span>}
        </div>
        <SocietePicker names={data.my_societes || []} value={soc.name} onChange={(n) => onSocieteChange?.(n)} dark />
        <p className="text-[11px] text-white/80 mt-1">{soc.rule}</p>
        {soc.representatives?.length > 1 && <p className="text-[10px] text-white/70 mt-1 flex items-center gap-1"><Users size={10} /> Représentants : {soc.representatives.join(', ')}</p>}
      </div>
      <DayCard day={day} societe={soc} onNewGuest={onNewGuest} />
      <MonthCard month={data.month} onNav={(delta) => setMonth(format(addMonths(parseISO(`${month}-01`), delta), 'yyyy-MM'))} />
      <div className="bg-white rounded-xl border p-3" data-testid="my-guests-section">
        <p className="text-xs font-semibold text-neutral-800 mb-2">Mes invités ({guests.length})</p>
        {guests.length === 0 ? <p className="text-[11px] text-neutral-500">Vos invités apparaîtront ici après votre première réservation invité.</p> : (
          <div className="space-y-1.5">
            {guests.map(g => (
              <div key={g.id} className="flex items-center justify-between gap-2 rounded-lg bg-neutral-50 px-2.5 py-1.5" data-testid={`my-guest-card-${g.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-neutral-800 truncate">{g.first_name} {g.last_name}</p>
                  <p className="text-[10px] text-neutral-500 truncate">Dernier départ {g.last_slot_date} à {g.last_slot_time} · {g.total_bookings} départ{g.total_bookings > 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => onRebookGuest(g)} className="px-2.5 py-1 rounded-full text-[11px] font-semibold text-white flex-shrink-0" style={{ backgroundColor: GREEN }} data-testid={`rebook-guest-${g.id}`}>Réserver</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export const SocieteGuestHint = ({ apiUrl, token, date, time, societe = '', onPreview }) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${apiUrl}/api/tee-times/portal/societe/summary?date=${date}&time=${time}&societe=${encodeURIComponent(societe)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(d => { if (alive) { setP(d); onPreview?.(d?.preview ? { ...d.preview, remaining_guests: d.day?.remaining_guests ?? 0, max_guests: d.day?.max_guests ?? 0, rep_slot_times: d.day?.rep_slot_times || [], remaining: d.day?.remaining ?? 0 } : null); } }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token, date, time, societe]);
  if (!p?.societe) return null;
  const { day, preview, societe: soc } = p;
  if (!soc.configured) return <p className="text-[11px] text-amber-700" data-testid="societe-guest-hint">{soc.rule}</p>;
  const ok = preview?.invitation;
  return (
    <div className={`rounded-lg px-2.5 py-2 text-[11px] ${ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`} data-testid="societe-guest-hint">
      <p className="font-semibold flex items-center gap-1"><Gift size={12} /> {ok ? `Green Fee de l'invité offert par ${soc.name}` : `Invitation non disponible sur ce créneau → Green Fee de l'invité payant${preview?.guest_price ? ` (${preview.guest_price} €)` : ''}`}</p>
      <p>Droit de jeu du jour : {day.used}/{day.quota} utilisé · {day.remaining_guests} invitation{day.remaining_guests > 1 ? 's' : ''} restante{day.remaining_guests > 1 ? 's' : ''}</p>
      {!ok && preview?.violations?.map((v, i) => <p key={i}>{v}</p>)}
    </div>
  );
};
